<template>
  <div class="incidents-grid">
    <attachment-sidepanel
      v-if="indentIdAttachOpen"
      class="sidepanel"
      :class="{'animation': animate}"
      :incident-id="indentIdAttachOpen"
    >
      <b-button
        v-shortkey="['shift', 'esc']"
        class="btn__icon btn__icon--primary"
        @click="animate=!animate"
        @shortkey.prevent="animate=!animate"
      >
        <i
          class="far fa-times"
          aria-hidden="true"
        />
      </b-button>
    </attachment-sidepanel>
    <b-row class="search-block  mb-3 mt-1">
      <b-col
        xl="4"
        lg="6"
        md="12"
      >
        <SearcherModule
          name="incidentSearcher"
          :error-message="$t('incidents.errors.validIdentifier')"
          @incidentSearch="onIncidentSearch($event)"
        />
      </b-col>
    </b-row>

    <!--Incident Grid-->
    <div class="grid-container">
      <GridHeader
        :gridtitle="sideBarState.selectedOption !== undefined ? $t(sideBarState.selectedOption.label) : ''"
        :gridsubtitle="msgSelectedRows"
        :qa="componentId"
      >
        <grid-display-options
          v-if="gridApi"
          :ui-component="gridUiComponent"
          @changedPageSize="changedPageSize($event)"
          @changedMode="changedMode($event)"
        />
        <div class="pl-1 pr-0 d-flex align-items-center">
          <button
            id="samplesCleanFilters"
            v-testing="'clean'"
            v-tooltip="$t('global.cleanFilter')"
            v-shortkey="['ctrl', 'alt', 'f']"
            type="button"
            class="btn btn__icon btn__icon--primary mx-2 p-0 btn-secondary"
            @shortkey.prevent="cleanFilters"
            @click="cleanFilters"
          >
            <i
              class="far fa-filter"
              aria-hidden="true"
            />
          </button>
        </div>

        <vt-report-export-buttons
          source="incidents-grid"
          :export-report="exportReport"
        />
      </GridHeader>

      <vt-grid
        ref="vtGrid"
        :key="rowModel"
        v-testing="'elements'"
        :short-key="shortKey"
        class="incidents-sample-grid mt-2"
        :row-model-type="rowModel"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-id="componentId"
        :component-code="uiComponent"
        :use-detail-custom-config="true"
        @column-visible="onColumnVisible"
      />
    </div>
    <add-custom-filter-modal
      id="addFilterModal"
      ref="addFilterModal"
      @ok="onAddFilterOk"
    />
    <common-attachment-tree-modal
      v-if="patient"
      ref="attachmentTreeModal"
      :patient="patient"
      :laboratory-id="laboratoryId"
    />
    <incident-form-modal
      v-if="coreEntity"
      id="incidenFormModal"
      ref="incidenFormModal"
      :core-entity="coreEntity"
      @incident-closed="onIncidentClosed($event)"
    />
    <print-component ref="printComponent" />
    <modal-confirmation
      id="optionChangeConfirmationModal"
      ref="optionChangeConfirmationModal"
      :content="$t('component.searcher.optionChangeConfirmation')"
    />
  </div>
</template>

<script>
import GridHeader from '@/components/GridHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import { incidentsGridColumnDefs } from '@/modules/incidentsModule/views/grid/config/incidents-grid.column-defs'
import { IncidentDatasource } from '@/modules/incidentsModule/views/grid/config/incident-datasource'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import SearcherModule from '@/components/SearcherModule'
import incidentSideBarStore from '../../sideBar/incidentSideBarStore'
import AddCustomFilterModal from '@/components/customFilter/AddCustomFilterModal'
import customFilterResource from '@/api/resources/ui/CustomFilterResource'
import ModalConfirmation from '@/components/modals/ModalConfirmation'
import { isEmpty } from 'lodash'
import CommonAttachmentTreeModal from '@/components/attachments/CommonAttachmentModalTree'
import AttachmentSidepanel from '@/components/common/AttachmentSidepanel'
import IncidentFormModal from '@/components/incidentsModal/IncidentFormModal'
import Encoder from '@/utils/encoder'
import incidentServices from '@/api/resources/incident/incidentServices'
import GridDisplayOptions from '@/components/GridDisplayOptions'
import { INCIDENT_TASK } from '@/modules/incidentsModule/sideBar/sideBarOptions'
import { GridUtils } from '@/shared/util/grid-utils'
import { GridExportMixin } from '@/mixins/grid-export.mixin'
import { AgGridRowModelType } from '@/shared/models/ag-grid.model'

const ROW_HEIGHT_NORMAL = 40
const ROW_HEIGHT_COMPACT = 25

export default {
    name: 'IncidentsGrid',
    components: {
        GridHeader,
        VtGrid,
        SearcherModule,
        ModalConfirmation,
        AddCustomFilterModal,
        CommonAttachmentTreeModal,
        AttachmentSidepanel,
        IncidentFormModal,
        GridDisplayOptions
    },
    mixins: [
        GridExportMixin
    ],
    props: {
        initMode: { type: Object, default: () => ({ value: 'compact', icon: 'fal fa-align-justify' }) },
        initFilterModel: { type: Object, default: null },
        initSortModel: { type: Array, default: () => [] },
        initPaginator: { type: Object, default: null },
        initTabId: { type: Number, default: 0 }
    },
    data() {
        return {
            mode: this.initMode,
            gridUiComponent: 'uicomponent.incidents.incidentgrid',
            animate: false,
            dataDetail: null,
            messageProcessModal: '',
            samId: null,
            showSidePanel: false,
            selectedIncident: null,
            indentIdAttachOpen: null,
            columnDefs: [],
            gridApi: null,
            columnApi: null,
            encoder: new Encoder(),
            validationRegex: [],
            sideBarState: incidentSideBarStore.state,
            patient: null,
            coreEntity: null,
            selectedRows: [],
            rowData: [],
            rowModel: AgGridRowModelType.SERVER_SIDE,
            optionChangedBecauseOfSearch: false,
            shortKey: {
                keys: { seeIncident: ['ctrl', 'enter'], seeAttachments: ['ctrl', 'a'] },
                callFn: this.shortcutsActions
            }
        }
    },
    computed: {
        gridOptions() {
            return {
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                sideBar: gridSideBarConfig,
                getRowHeight: ({ data, api }) => {
                    let rowHeight = this.rowHeight
                    const notifiedPersonelColumnIsDisplayed = api.columnModel.displayedColumns.some((item) => { return item.colId === 'notifiedPersonnelList.id' })
                    if (data?.notifiedPersonnelList.length && notifiedPersonelColumnIsDisplayed) {
                        rowHeight *= data.notifiedPersonnelList.length
                    }
                    return rowHeight
                },
                getContextMenuItems: this.getContextMenuItems,
                onRowDoubleClicked: (row) => this.showIncidentFormModal(row.data),
                autoGroupColumnDef: {
                    headerName: 'Consoles', // custom header name for group
                    pinned: 'left', // force pinned left. Does not work in columnDef
                    cellRendererParams: {
                        suppressCount: true// remove number in Group Column
                    }
                }
            }
        },
        laboratoryId() {
            return this.$store.getters.labId
        },
        sideBarFilterModel() {
            return this.sideBarState?.selectedOption?.filterModel ?? {}
        },
        sideBar() {
            // changes when the sideBar is expanded or contracted
            return this.sideBarState.sideBar
        },
        rowHeight() {
            if (this.mode.value === 'compact') {
                return ROW_HEIGHT_COMPACT
            }
            return ROW_HEIGHT_NORMAL
        },
        sortModel() {
            return this.gridApi.getSortModel()
        },
        msgSelectedRows() {
            if (this.selectedRows.length) {
                const choice = this.selectedRows.length > 1 ? 2 : 1
                return `${this.selectedRows.length} ${this.$tc('global.selectedElements', choice)}`
            }
            return ''
        },
        selectedOption() {
            return this.sideBarState.selectedOption
        },
        componentId() {
            return this.selectedOption?.id?.toString()
        },
        uiComponent() {
            return this.selectedOption?.uicomponent
        }
    },
    watch: {
        sideBarFilterModel() {
            this.indentIdAttachOpen = null
        },
        sideBar() {
            this.gridApi?.sizeColumnsToFit()
        },
        selectedOption() {
            if (this.optionChangedBecauseOfSearch) {
                this.optionChangedBecauseOfSearch = false
                return
            }

            this.rowModel = AgGridRowModelType.SERVER_SIDE
            this.rowData = []
        }
    },
    created() {
        if (this.$route.params.sidebarOption) {
            const option = incidentSideBarStore.findOptionById(this.$route.params.sidebarOption)
            incidentSideBarStore.setSelectedOption(option)
        } else if (this.$route.params.filterModel) {
            this.initFilterModel = this.$route.params.filterModel
        } else {
            incidentSideBarStore.selectDefaultOption()
        }
    },
    methods: {
        onColumnVisible() {
            const notifiedPersonelColumnIsDisplayed = this.gridApi.columnModel.displayedColumns.some((item) => { return item.colId === 'notifiedPersonnelList.id' })
            this.gridApi.forEachNode((rowNode) => {
                let rowHeight = this.rowHeight
                if (rowNode.data) {
                    if (rowNode.data?.notifiedPersonnelList.length && notifiedPersonelColumnIsDisplayed) {
                        rowHeight *= rowNode.data.notifiedPersonnelList.length
                        rowNode.setRowHeight(rowHeight)
                    } else {
                        rowNode.setRowHeight(this.mode.size)
                    }
                }
            })
            this.gridApi.onRowHeightChanged()
        },
        async onIncidentSearch(searchInformation) {
            let filterModel = {
                incLabel: GridUtils.getTextFilterModel('equals', searchInformation.search)
            }
            const params = {
                page: 0,
                size: 1
            }
            const [error, paginator] = await this.toCall(incidentServices.paginator(filterModel, params, INCIDENT_TASK.RELATED_TO_ME.url))
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
                return
            } else if (paginator.totalElements === 0) {
                this.showErrorModalGlobal(this.$t('component.searcher.error.noIncident'))
                return
            }
            const foundIncident = paginator.content[0]
            if (this.rowData.find(incident => incident.incidentId === foundIncident.incidentId)) {
                this.showErrorModalGlobal(this.$t('component.searcher.error.elementAlreadyAdded'))
                return
            }
            const matchingSideBarOption = this._getSidebarOptionThatMatchesIncidentStatus(foundIncident)
            const previousNodes = this.gridApi.getRenderedNodes()
            if (!matchingSideBarOption.includes(this.selectedOption)) {
                if (this.rowData.length >= 1) {
                    this.$refs.optionChangeConfirmationModal.ask(() => {
                        // on ok callback
                        incidentSideBarStore.setSelectedOption(matchingSideBarOption[0])
                        this.optionChangedBecauseOfSearch = true
                        this.rowData = [foundIncident]
                        this.gridApi.setRowData(this.rowData)
                        this.$_keepSelectionGridWhenSearch(this.gridApi, foundIncident.incidentId, previousNodes, 'incidentId')
                    })
                    return
                } else {
                    this.optionChangedBecauseOfSearch = true
                    incidentSideBarStore.setSelectedOption(matchingSideBarOption[0])
                }
            }
            this.rowData.push(foundIncident)
            if (this.rowModel === AgGridRowModelType.SERVER_SIDE) {
                this.rowModel = AgGridRowModelType.CLIENT_SIDE
            } else {
                this.gridApi.setRowData(this.rowData)
            }
            this.$_keepSelectionGridWhenSearch(this.gridApi, foundIncident.incidentId, previousNodes, 'incidentId')
        },
        _getSidebarOptionThatMatchesIncidentStatus(incident) {
            if (incident.closingDate) {
                return [incidentSideBarStore.findOptionById('closedAndRelatedToMe')]
            }
            const matchingSideBarOptions = []
            const currentPersonnelId = this.$store.getters.userInformation?.personnel?.id
            if (incident.openedBy.id === currentPersonnelId) {
                matchingSideBarOptions.push(incidentSideBarStore.findOptionById('stillOpenAndOpenedByMe'))
            }
            if (incident.notifiedPersonnelList.find(notified => notified.id === currentPersonnelId)) {
                matchingSideBarOptions.push(incidentSideBarStore.findOptionById('stillOpenAndNotifiedToMe'))
            }
            return matchingSideBarOptions
        },
        async onSideBarClickUpdateFilter(option) {
            if (isEmpty(this.gridApi.getFilterModel())) {
                this.saveNoFilter()
                return
            }
            const params = { filterModel: this.gridApi.getFilterModel(), task: this.selectedOption.task, uicomponent: this.selectedOption.uicomponent }
            const [err] = await this.toCall(incidentSideBarStore.updateCustomOption(option, params))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            await incidentSideBarStore.loadCustomOptions()
            this.showToastSuccessGlobal(this.$t('global.filterUpdated'))
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        exportReport(exportType) {
            const columns = this.$_getColumnsToExport(this.gridApi, this.columnApi)
            if (columns && this.gridApi) {
                return incidentServices.exportTo(
                    this.$tc(this.sideBarState.selectedOption.label),
                    this.gridApi.getFilterModel(),
                    GridUtils.getSortValue(this.sortModel),
                    columns,
                    this.selectedRows.map(incident => incident.incidentId),
                    this.selectedOption.task.url,
                    exportType)
            }
        },
        shortcutsActions(event) {
            if (this.selectedRows.length === 1) {
                const selection = this.selectedRows[0]
                switch (event.srcKey) {
                case 'seeIncident':
                    this.showIncidentFormModal(selection)
                    break
                case 'seeAttachments':
                    this.showAttachments(selection)
                    break
                }
            }
        },
        _getRowData() {
            this._setDataSource()
            this.$refs.vtGrid.loadCustomConfig()
        },
        onGridReady(event) {
            this.gridApi = event.api
            this.columnApi = event.columnApi

            this.gridApi.setColumnDefs(incidentsGridColumnDefs(params => this.showAttachments(params.data)))
            if (this.rowModel === AgGridRowModelType.SERVER_SIDE) {
                this.searchActive = false
                this._setDataSource()
                // this._getRowData()
            } else {
                this.gridApi.setRowData(this.rowData)
                this.gridApi.selectAll()
            }
        },
        _setDataSource() {
            if (this.gridApi) {
                if (this.initFilterModel) {
                    this.gridApi.setFilterModel(this.initFilterModel)
                    this.initFilterModel = null
                    return
                }

                if (this.sideBarFilterModel) {
                    this._applySideBarFilterModel()
                }

                this._reloadRowData()
            }
        },
        showAttachments(data) {
            this.animate = true
            this.indentIdAttachOpen = data.incidentId
        },
        showIncidentFormModal(data) {
            const entityIds = {
                patId: data.patientId,
                requestId: data.requestId,
                casId: data.casId,
                samId: data.samId,
                porId: data.porId,
                supId: data.supId
            }
            this.coreEntity = data.coreEntity.code
            this.$nextTick(() => {
                this.$refs.incidenFormModal.show(null, entityIds, data.incidentId)
            })
        },
        getContextMenuItems(params) {
            if (params?.node) {
                const selected = params.node.data
                const isMultipleSelection = this.selectedRows.length > 1
                return [
                    {
                        name: this.$tc('component.attachModal.viewAttach'),
                        action: () => this.showAttachments(selected),
                        cssClasses: isMultipleSelection ? ['not-show'] : ''
                    },
                    {
                        name: selected.closingDate ? this.$tc('incidents.shortcuts.seeIncident') : this.$tc('incidents.grid.viewIncident'),
                        action: () => this.showIncidentFormModal(selected),
                        cssClasses: isMultipleSelection ? ['not-show'] : ''
                    }
                ]
            }
            return []
        },
        showAttachmentTreeModal(incident) {
            const coreEntity = incident.coreEntity.code
            switch (coreEntity) {
            case 'PATIENT':
                this._internalShowAttachmentModal(incident, () => this.$refs.attachmentTreeModal.showPatient())
                break
            case 'CASE':
                this._internalShowAttachmentModal(incident, () => this.$refs.attachmentTreeModal.showCase(incident.entityId))
                break
            case 'SAMPLE':
                this._internalShowAttachmentModal(incident, () => this.$refs.attachmentTreeModal.showSample(incident.entityId))
                break
            case 'PORTION':
                this._internalShowAttachmentModal(incident, () => this.$refs.attachmentTreeModal.showPortion(incident.entityId))
                break
            case 'SUBPORTION':
                this._internalShowAttachmentModal(incident, () => this.$refs.attachmentTreeModal.showSubportion(incident.entityId))
                break
            }
        },
        _internalShowAttachmentModal(incident, show) {
            if (incident.attachments) {
                this.patient = incident.patient
                this.patient.centerIdentifier = IncidentDatasource.getPatientCenterIdentifier(incident.patient)
                this.$nextTick(() => {
                    show()
                })
            }
        },
        _reloadRowData() {
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(new IncidentDatasource(this.selectedOption.task.url))
        },
        _applySideBarFilterModel() {
            if (this.gridApi) {
                this.gridApi.setFilterModel(this.sideBarFilterModel)
            }
        },
        changedMode(mode) {
            this.mode = mode
            this.onColumnVisible()
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        cleanFilters() {
            this._applySideBarFilterModel()
        },
        onSideBarClickAddFilter() {
            this.$refs.addFilterModal.show()
        },
        async onAddFilterOk(filter) {
            const params = { label: filter.label, filterModel: this.gridApi.getFilterModel(), task: this.selectedOption.task, uicomponent: this.selectedOption.uicomponent }
            await customFilterResource.create(this.sideBarState.componentCode, params)
            await incidentSideBarStore.loadCustomOptions()
            this.showToastSuccessGlobal(this.$t('global.filterAdded'))
        },
        onIncidentClosed(incident) {
            if (this.rowModel === AgGridRowModelType.CLIENT_SIDE) {
                const index = this.rowData.findIndex(rowData => rowData.incidentId === incident?.incidentId)
                if (index > -1) {
                    this.rowData.splice(index, 1)
                    this.gridApi.setRowData(this.rowData)

                    if (!this.rowData.length) {
                        this.searchActive = false
                        this.rowModel = AgGridRowModelType.SERVER_SIDE
                        this.$nextTick(() => this._reloadRowData())
                    }
                }
            } else {
                this._reloadRowData()
            }

            this.$emit('incident-closed', event)
        }
    }
}
</script>

<style lang="scss">
.incidents-grid .sr-panel-mid-section {
    margin-bottom: 0;
}

.incidents-grid .sidepanel {
    border-left: 1px solid #dee2e6;
    background: #FFF;
    width: 300px;
    position: fixed;
    top: 0;
    right: -300px;
    z-index: 3;
    transition: .3s;
    box-shadow: 0 3px 5px 0 #9c9c9c;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding-top: 45px;
}

.incidents-grid .sidepanel.animation {
    right: 0;
}

.incidents-grid .sidepanel .header {
    border-bottom: 1px solid #dee2e6;
}

.incidents-grid .sidepanel .title {
    color: vt-color(primary);
}

.incidents-grid .sidepanel .content {
    flex-grow: 1;
    overflow: hidden;
    overflow-y: auto;
}

.incidents-grid .sidepanel .file {
    border-bottom: 1px solid #dee2e6;
}

.incidents-grid .sidepanel .file .others {
    color: grey;
    font-size: .9rem;
}

.incidents-grid .sidepanel .element .col-md-4 {
    max-width: 100% !important;
    padding-left: 10px;
    margin-top: 5px;
    margin-bottom: 0 !important;
}

.incidents-grid .grossing-sample-page {
    display: flex;
}

.incidents-grid .sr-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0.5rem;
    flex-shrink: 0;
}

.incidents-grid .incidents-sample-grid {
    height: 100%;
}
</style>
